<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
// NOTICE: KAPTION-INTEGRATION — Secure Assignment Mode leader selection
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';

import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import AgentSelector from '../AgentSelector.vue';
// NOTICE: KAPTION-INTEGRATION — group-visibility.GUARDRAIL.1/.2/.3
import KaptionVisibilityWarning from '../KaptionVisibilityWarning.vue';
import { useVuelidate } from '@vuelidate/core';

export default {
  components: {
    PageHeader,
    AgentSelector,
    KaptionVisibilityWarning,
  },
  validations: {
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
  },

  setup() {
    // NOTICE: KAPTION-INTEGRATION
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    return { v$: useVuelidate(), isKaptionFlagEnabled };
  },

  data() {
    return {
      selectedAgents: [],
      // NOTICE: KAPTION-INTEGRATION — group-visibility.LEADER.1
      selectedLeaders: [],
      isCreating: false,
    };
  },

  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
    }),

    teamId() {
      return this.$route.params.teamId;
    },
    headerTitle() {
      return this.$t('TEAMS_SETTINGS.ADD.TITLE', {
        teamName: this.currentTeam.name,
      });
    },
    currentTeam() {
      return this.$store.getters['teams/getTeam'](this.teamId);
    },
    // NOTICE: KAPTION-INTEGRATION — leader column only for restricted teams while
    // the feature is available (KAPTION_GROUP_VISIBILITY).
    showLeaderColumn() {
      return (
        this.isKaptionFlagEnabled('KAPTION_GROUP_VISIBILITY') &&
        this.currentTeam.group_visibility === 'restricted'
      );
    },
  },

  mounted() {
    this.$store.dispatch('agents/get');
  },

  methods: {
    updateSelectedAgents(newAgentList) {
      this.v$.selectedAgents.$touch();
      this.selectedAgents = [...newAgentList];
    },
    // NOTICE: KAPTION-INTEGRATION
    updateSelectedLeaders(newLeaderList) {
      this.selectedLeaders = [...newLeaderList];
    },
    selectAllAgents() {
      this.selectedAgents = this.agentList.map(agent => agent.id);
    },
    async addAgents() {
      // NOTICE: KAPTION-INTEGRATION — group-visibility.GUARDRAIL.3: confirm-to-proceed when the
      // people being added have an effective visibility wider than this team's
      // classification implies. Resolves true immediately when there is nothing
      // to warn about.
      const confirmed =
        await this.$refs.visibilityWarning?.requestConfirmation();
      if (confirmed === false) return;

      this.isCreating = true;
      const { teamId, selectedAgents, selectedLeaders } = this;

      try {
        await this.$store.dispatch('teamMembers/create', {
          teamId,
          agentsList: selectedAgents,
          // NOTICE: KAPTION-INTEGRATION — group-visibility.API.4
          leadersList: this.showLeaderColumn ? selectedLeaders : undefined,
        });
        router.replace({
          name: 'settings_teams_finish',
          params: {
            page: 'new',
            teamId,
          },
        });
        this.$store.dispatch('teams/get');
      } catch (error) {
        useAlert(error.message);
      }
      this.isCreating = false;
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <form
      class="flex flex-wrap mx-0 overflow-x-auto"
      @submit.prevent="addAgents"
    >
      <div class="w-full">
        <PageHeader
          :header-title="headerTitle"
          :header-content="$t('TEAMS_SETTINGS.ADD.DESC')"
        />
      </div>

      <div class="w-full">
        <div v-if="v$.selectedAgents.$error">
          <p class="error-message pb-2">
            {{ $t('TEAMS_SETTINGS.ADD.AGENT_VALIDATION_ERROR') }}
          </p>
        </div>
        <AgentSelector
          :agent-list="agentList"
          :selected-agents="selectedAgents"
          :update-selected-agents="updateSelectedAgents"
          :show-leader-column="showLeaderColumn"
          :selected-leaders="selectedLeaders"
          :update-selected-leaders="updateSelectedLeaders"
          :is-working="isCreating"
          :submit-button-text="$t('TEAMS_SETTINGS.ADD.BUTTON_TEXT')"
        />
        <!-- NOTICE: KAPTION-INTEGRATION — group-visibility.GUARDRAIL.1/.2 -->
        <KaptionVisibilityWarning
          ref="visibilityWarning"
          :agent-ids="selectedAgents"
          :agent-list="agentList"
          :team-visibility="currentTeam.group_visibility"
          :team-name="currentTeam.name"
        />
      </div>
    </form>
  </div>
</template>
